import { Button } from '@cargoflow/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@cargoflow/ui/components/card'
import { Input } from '@cargoflow/ui/components/input'
import { Label } from '@cargoflow/ui/components/label'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { assertAdminOrSignOut } from '@/lib/assert-admin'
import { getSession, signIn } from '@/lib/auth-client'

const ZodSignInSearch = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/sign-in')({
  validateSearch: ZodSignInSearch,
  beforeLoad: async () => {
    const session = await getSession()
    if (!session.data) return
    const admin = await assertAdminOrSignOut()
    if (admin) {
      throw redirect({ to: '/companies' })
    }
  },
  component: SignInPage,
})

function SignInPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const { error } = await signIn.email({ email, password })
      if (error) {
        toast.error(error.message ?? 'Sign in failed')
        return
      }
      const admin = await assertAdminOrSignOut()
      if (!admin) {
        toast.error('This account does not have admin access')
        return
      }
      navigate({ to: search.redirect ?? '/companies' })
    } catch (_err) {
      toast.error('Sign in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>CargoFlow Back Office</CardTitle>
          <CardDescription>Sign in to the admin console</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
